import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  getErrorMessage,
  hasValidationErrors,
  setValidationErrors,
} from '../../../../../common/global/helpers';
import {
  LoadingObject,
  LoadingState,
} from '../../../../../common/global/models';
import Button from '../../../../../common/widgets/Button';
import Dialog from '../../../../../common/widgets/Dialog';
import Icons from '../../../../../common/widgets/Icons';
import {
  Notification,
  NotificationType,
} from '../../../../../common/widgets/Notification';
import { ErrorState, ErrorType } from '../../../models/Errors';
import { SubscriptionItemModel } from '../../../models/Subscriptions';
import { resources } from '../../../resources/resources';
import { employeeBenefitsService } from '../../../services/employee-choices';
import { productsService } from '../../../services/products';

interface CancelSubscriptionDialogProps {
  product: SubscriptionItemModel;
  onClose: () => any;
  onSuccess: () => any;
  context?: 'benefit' | 'admin';
}

export default function CancelSubscriptionDialog(
  props: Readonly<CancelSubscriptionDialogProps>,
) {
  const { t: translate } = useTranslation();
  const isCancellingRef = useRef(false);
  const [result, setResult] = useState<
    LoadingObject<{
      hasErrors: boolean;
      errorCode?: string;
    }>
  >();
  const [errors, setErrors] = useState<ErrorState>({});

  const handleCancelClick = async () => {
    if (isCancellingRef.current) return;

    isCancellingRef.current = true;
    setResult({ state: LoadingState.Requested });
    let r = undefined;
    if (props.context === 'admin') {
      r = await employeeBenefitsService.cancelSubscription(
        props.product.orderProductId,
      );
    } else {
      r = await productsService.cancelSubscription(
        props.product.orderProductId,
      );
    }
    if (r.hasErrors) {
      if (r.errorType === ErrorType.ValidationError) {
        setValidationErrors(r, setErrors);
      }
    }

    setResult({
      value: { hasErrors: r.hasErrors, errorCode: r.errorCode },
      state: LoadingState.Ready,
    });

    isCancellingRef.current = false;
  };

  const customClose = () => {
    if (result?.state === LoadingState.Ready && !result.value?.hasErrors) {
      props.onSuccess();
    }

    props.onClose();
  };

  if (result?.value?.hasErrors === false) {
    return (
      <Dialog
        closeDialog={customClose}
        closeOnClickOutside
        showCloseIcon
        className="dialog-s"
      >
        <div className="dialog-content">
          <div className="dialog-image">
            <Icons.Success />
          </div>
          <div className="dialog-title">
            {translate(resources.recurring.success.title)}
          </div>
          <div className="dialog-text centered">
            {translate(resources.recurring.success.message)}
          </div>
          <div className="dialog-actions vertical">
            <Button
              onClick={customClose}
              className="btn btn-outline"
              disabled={result?.state === LoadingState.Requested}
            >
              {translate(resources.close)}
            </Button>
          </div>
        </div>
      </Dialog>
    );
  }

  return (
    <Dialog
      closeDialog={customClose}
      closeOnClickOutside
      showCloseIcon
      className="dialog-s"
    >
      <div className="dialog-content">
        <div className="dialog-image">
          <Icons.HelpRed />
        </div>
        <div className="dialog-title">
          {translate(resources.recurring.cancelMessage)}
        </div>
        {result?.value?.hasErrors === true && (
          <div className="dialog-text centered">
            {result?.value?.errorCode && translate(result?.value?.errorCode)}
          </div>
        )}
      </div>
      {hasValidationErrors(errors, 'null') && (
        <Notification
          type={NotificationType.Error}
          messages={errors.null.map(e => ({
            message: getErrorMessage(e, translate),
            onShowDetailsClick: undefined,
          }))}
        ></Notification>
      )}
      <div className="dialog-actions vertical">
        <Button
          onClick={handleCancelClick}
          className="btn btn-solid"
          loading={result?.state === LoadingState.Requested}
        >
          {translate(resources.recurring.cancel)}
        </Button>
        <Button
          onClick={customClose}
          className="btn btn-outline"
          disabled={result?.state === LoadingState.Requested}
        >
          {translate(resources.close)}
        </Button>
      </div>
    </Dialog>
  );
}
